import {
  AppModal,
  DateFormField,
  Form,
  FormSubmitButton,
  SelectFormField,
  Typography,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useApplyMatterLitigationDatePlan } from "@/lib/data/react-query/matters/matter-litigation-date-plan";
import { retrieveMatterLitigationDatePlanDatesQueryOptions } from "@/lib/data/react-query/matters/matter-litigation-date-plan-date";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { MatterLitigationDatePlanDate } from "@/lib/types";
import { LitigationPlanTriggersAccordion } from "./LitigationPlanTriggersAccordion";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { cn } from "@/lib/utils/cn";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

interface MatterLitigationDatePlanConnectionModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
}

const triggerDeadlineSchema = z.object({
  typeId: z.string().min(1, { message: "Litigation date type is required" }),
  deadlineDate: z.string().min(1, { message: "Deadline date is required" }),
});

const litigationDatePlanConnectionFormSchema = z.object({
  planId: z.string().min(1, { message: "Plan is required" }),
  triggerDeadlines: z
    .array(triggerDeadlineSchema)
    .min(1, { message: "At least one starting trigger is required" }),
});

type LitigationDatePlanConnectionFormValues = z.infer<
  typeof litigationDatePlanConnectionFormSchema
>;

const defaultFormValues: LitigationDatePlanConnectionFormValues = {
  planId: "",
  triggerDeadlines: [],
};

const findStartingTriggerTypeIds = (
  planDates: MatterLitigationDatePlanDate[],
) => {
  const triggerIds = new Set<string>();
  const targetIds = new Set<string>();

  planDates.forEach((planDate) => {
    const triggerId = planDate.triggerId.toString().trim();
    const targetId = planDate.typeId.toString().trim();

    if (triggerId) {
      triggerIds.add(triggerId);
    }

    if (targetId) {
      targetIds.add(targetId);
    }
  });

  if (triggerIds.size === 0) {
    return [];
  }

  const roots = [...triggerIds].filter((id) => !targetIds.has(id));
  return roots.length > 0 ? roots : [...triggerIds];
};

export const MatterLitigationDatePlanConnectionModal = ({
  title,
  description,
  open,
  setOpen,
  matterId,
}: MatterLitigationDatePlanConnectionModalProps) => {
  const litigationDatePlanConnectionModalFormId =
    "litigation-date-plan-connection-modal-form";

  const { hasActionPermission } = useUser();
  const applyPlan = useApplyMatterLitigationDatePlan();
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasSettingsPermission = Boolean(true);

  const matterSettingsQueryOptions = retrieveMatterSettingsQueryOptions();
  const { data: matterSettingsData, isLoading: isMatterSettingsLoading } =
    useQuery({
      ...matterSettingsQueryOptions,
      enabled: hasSettingsPermission,
    });

  const litigationDatePlans =
    matterSettingsData?.data.litigationDatePlans ?? [];
  const rawLitigationDateTypes = matterSettingsData?.data.litigationDateTypes;

  const litigationDateTypes = useMemo(
    () =>
      [...(rawLitigationDateTypes ?? [])].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      ),
    [rawLitigationDateTypes],
  );

  const form = useForm<LitigationDatePlanConnectionFormValues>({
    resolver: zodResolver(litigationDatePlanConnectionFormSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const { fields, replace } = useFieldArray({
    control: form.control,
    name: "triggerDeadlines",
  });

  const selectedPlanId = form.watch("planId");

  const { data: planDatesData, isLoading: isPlanDatesLoading } = useQuery({
    ...retrieveMatterLitigationDatePlanDatesQueryOptions(selectedPlanId),
    enabled: hasSettingsPermission && !!selectedPlanId,
  });

  const planDates: MatterLitigationDatePlanDate[] = useMemo(() => {
    const raw = planDatesData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [planDatesData]);

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues);
      replace([]);
    }
  }, [form, open, replace]);

  const startingTriggers = useMemo(() => {
    if (!selectedPlanId || planDates.length === 0) {
      return [];
    }

    const startingTriggerTypeIds = findStartingTriggerTypeIds(planDates);

    return startingTriggerTypeIds
      .map((typeId) => {
        const match = litigationDateTypes.find((type) => type.id === typeId);
        if (!match) return null;
        return {
          id: typeId,
          label: match.label || typeId,
        };
      })
      .filter(
        (
          trigger,
        ): trigger is {
          id: string;
          label: string;
        } => trigger !== null,
      );
  }, [litigationDateTypes, planDates, selectedPlanId]);

  const missingStartingTriggerTypeIds = useMemo(() => {
    if (!selectedPlanId || planDates.length === 0) {
      return [];
    }

    const startingTriggerTypeIds = findStartingTriggerTypeIds(planDates);
    const knownTypeIds = new Set(litigationDateTypes.map((type) => type.id));

    return startingTriggerTypeIds.filter((typeId) => !knownTypeIds.has(typeId));
  }, [litigationDateTypes, planDates, selectedPlanId]);

  const canSubmitPlan =
    hasCreatePermission &&
    !!selectedPlanId &&
    startingTriggers.length > 0 &&
    !isPlanDatesLoading &&
    !isMatterSettingsLoading;

  const triggerLabelMap = useMemo(() => {
    return startingTriggers.reduce<Record<string, string>>((acc, trigger) => {
      acc[trigger.id] = trigger.label;
      return acc;
    }, {});
  }, [startingTriggers]);

  useEffect(() => {
    if (!selectedPlanId) {
      replace([]);
      return;
    }

    if (startingTriggers.length === 0) {
      replace([]);
      return;
    }

    const deadlines = startingTriggers.map((trigger) => ({
      deadlineDate: "",
      typeId: trigger.id,
    }));

    replace(deadlines);
  }, [replace, selectedPlanId, startingTriggers]);

  const handleSubmit = async (
    values: LitigationDatePlanConnectionFormValues,
  ) => {
    if (!hasCreatePermission) return;

    const dates = values.triggerDeadlines.map((deadline) => ({
      deadlineDate: deadline.deadlineDate,
      typeId: deadline.typeId,
    }));

    const payload = {
      dates,
      matterId,
      planId: values.planId,
    };

    await applyPlan.mutateAsync(payload);

    // Notify the litigation page that a case plan is being constructed
    window.dispatchEvent(
      new CustomEvent("matter-litigation-case-plan-constructing", {
        detail: { matterId },
      }),
    );

    form.reset(defaultFormValues);
    setOpen(false);
  };

  const isLoading = applyPlan.isPending;

  const modalFieldsetClassName = cn(
    slideoverFieldsetClassName,
    "my-0 px-0 pt-0 pb-4",
  );

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        size="2xl"
        loading={isLoading}
        isDirty={form.formState.isDirty}
        bodyFormId={litigationDatePlanConnectionModalFormId}
        bodyFormOnSubmit={form.handleSubmit(handleSubmit)}
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              submitFormId={litigationDatePlanConnectionModalFormId}
              loading={isLoading}
              disabled={!canSubmitPlan}
            />
          </div>
        }
      >
        <div className="space-y-6">
          <fieldset
            className={modalFieldsetClassName}
            disabled={!hasCreatePermission}
          >
            <SelectFormField
              items={litigationDatePlans}
              itemsKey="id"
              formatItemLabel={(item) => item?.label}
              register={form.register("planId")}
              label="Litigation date plan*"
              placeholder={
                hasSettingsPermission
                  ? "Select a litigation date plan"
                  : "You do not have access to plans"
              }
              isItemsLoading={isMatterSettingsLoading}
            />
            <div className="col-span-2">
              {selectedPlanId && (
                <LitigationPlanTriggersAccordion
                  planDates={planDates}
                  litigationDateTypes={litigationDateTypes}
                  isLoading={isPlanDatesLoading || isMatterSettingsLoading}
                />
              )}
            </div>
          </fieldset>

          {selectedPlanId && (
            <fieldset
              className={modalFieldsetClassName}
              disabled={!hasCreatePermission}
            >
              <div className="col-span-2">
                <Typography weight="semibold">
                  Starting litigation triggers
                </Typography>
                <Typography variant="default" size="sm">
                  Select a deadline date for each starting trigger derived from
                  this plan.
                </Typography>
              </div>
              {fields.length === 0 ? (
                <div className="col-span-2 space-y-2">
                  <Typography size="sm">
                    {isPlanDatesLoading || isMatterSettingsLoading
                      ? "Loading starting triggers..."
                      : planDates.length === 0
                        ? "This plan has no date rules yet. Expand the plan in Matter Settings → Litigation Plans and add at least one plan date before applying it to a matter."
                        : missingStartingTriggerTypeIds.length > 0
                          ? "This plan references litigation date types that no longer exist. Update the plan date rules in Matter Settings → Litigation Plans before applying."
                          : "No starting triggers available for the selected plan."}
                  </Typography>
                </div>
              ) : (
                <div className="col-span-2 space-y-4">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="border-border rounded-lg border p-4 shadow-sm"
                    >
                      <Typography weight="semibold">
                        {triggerLabelMap[field.typeId] ??
                          `Trigger ${index + 1}`}
                      </Typography>
                      <DateFormField
                        register={form.register(
                          `triggerDeadlines.${index}.deadlineDate`,
                        )}
                        name={`triggerDeadlines.${index}.deadlineDate`}
                        label="Deadline date*"
                      />
                    </div>
                  ))}
                </div>
              )}
            </fieldset>
          )}
        </div>
      </AppModal>
    </Form>
  );
};
